import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentModal'
import {AssigneeRepositoryPicker, type AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import type {IssueListItem} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import {LabelPicker, type LabelPickerLabel} from '@github-ui/item-picker-label-tsq'
import {
  IssueClosedIcon,
  IssueOpenedIcon,
  IssueReopenedIcon,
  PeopleIcon,
  SkipIcon,
  TagIcon,
  TriangleDownIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button} from '@primer/react'
import {useCallback, useMemo, useRef, useState} from 'react'

import {RepoIssuesBulkPickerAnchor, type RepoIssuesBulkActionProps} from './RepoIssuesBulkActionShared'

export function RepoIssuesBulkMarkAs({disabled, nested, onUpdate}: Omit<RepoIssuesBulkActionProps, 'owner' | 'repo'>) {
  const [isOpen, setIsOpen] = useState(false)
  const options = [
    {label: 'Open', update: {state: 'OPEN'} as const, icon: IssueOpenedIcon},
    {label: 'Completed', update: {state: 'CLOSED', stateReason: 'COMPLETED'} as const, icon: IssueClosedIcon},
    {label: 'Not planned', update: {state: 'CLOSED', stateReason: 'NOT_PLANNED'} as const, icon: SkipIcon},
  ]
  const items = options.map(option => (
    <ActionList.Item
      key={option.label}
      aria-disabled={disabled || undefined}
      onSelect={() => {
        if (disabled) return
        onUpdate(option.update)
        setIsOpen(false)
      }}
    >
      <ActionList.LeadingVisual>
        <option.icon />
      </ActionList.LeadingVisual>
      {option.label}
    </ActionList.Item>
  ))
  const anchor = nested ? (
    <ActionList.Item aria-disabled={disabled || undefined}>
      <ActionList.LeadingVisual>
        <IssueReopenedIcon />
      </ActionList.LeadingVisual>
      Mark as
    </ActionList.Item>
  ) : (
    <Button aria-disabled={disabled || undefined} leadingVisual={IssueReopenedIcon} trailingVisual={TriangleDownIcon}>
      Mark as
    </Button>
  )

  return (
    <ActionMenu open={isOpen} onOpenChange={open => !disabled && setIsOpen(open)}>
      <ActionMenu.Anchor>{anchor}</ActionMenu.Anchor>
      <ActionMenu.Overlay>
        <ActionList>{items}</ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

export function RepoIssuesBulkLabels({
  commonLabels,
  disabled,
  nested,
  owner,
  repo,
  onUpdate,
}: RepoIssuesBulkActionProps & {commonLabels: Array<{id: string; name: string}>}) {
  const anchorElement = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => (
      <RepoIssuesBulkPickerAnchor {...{anchorProps, disabled, nested}} icon={TagIcon} label="Label" />
    ),
    [disabled, nested],
  )
  const handleSelectionChange = useCallback(
    (selectedLabels: LabelPickerLabel[]) => {
      const commonIds = new Set(commonLabels.map(label => label.id))
      const selectedIds = new Set(selectedLabels.map(label => label.id))
      const addedLabelIds = [...selectedIds].filter(id => !commonIds.has(id))
      const removedLabelIds = [...commonIds].filter(id => !selectedIds.has(id))
      if (addedLabelIds.length > 0 || removedLabelIds.length > 0) {
        onUpdate({addedLabelIds, removedLabelIds})
      }
    },
    [commonLabels, onUpdate],
  )

  return (
    <LabelPicker
      anchorElement={anchorElement}
      title="Apply labels"
      showEditLabelsButton={false}
      repo={repo}
      owner={owner}
      readonly={disabled}
      onSelectionChange={handleSelectionChange}
      labels={[]}
      labelNames={commonLabels.map(label => label.name)}
      canCreateLabel={false}
      nested={nested}
    />
  )
}

export function RepoIssuesBulkAssignees({
  commonAssignees,
  disabled,
  nested,
  owner,
  repo,
  selectedIssues,
  onUpdate,
}: RepoIssuesBulkActionProps & {
  commonAssignees: AssigneePickerAssignee[]
  selectedIssues: Array<Pick<IssueListItem, 'assignees' | 'number'>>
}) {
  const [copilotModalOpen, setCopilotModalOpen] = useState(false)
  const [copilotAssigneeId, setCopilotAssigneeId] = useState<string>()
  const [pickerInstanceKey, setPickerInstanceKey] = useState(0)
  const assignButtonRef = useRef<HTMLElement>(null)
  const canUseCopilotAssignment = !commonAssignees.some(assignee => assignee.isCopilot)
  const issueNumbersWithoutCopilot = useMemo(
    () =>
      selectedIssues
        .filter(issue => !issue.assignees.some(assignee => String(assignee.id) === copilotAssigneeId))
        .map(issue => issue.number),
    [copilotAssigneeId, selectedIssues],
  )
  const anchorElement = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>, anchorRef?: React.Ref<HTMLButtonElement | HTMLLIElement>) => (
      <RepoIssuesBulkPickerAnchor
        {...{anchorProps, disabled, nested}}
        anchorRef={element => {
          assignButtonRef.current = element
          if (typeof anchorRef === 'function') anchorRef(element)
          else if (anchorRef) anchorRef.current = element
        }}
        icon={PeopleIcon}
        label="Assign"
      />
    ),
    [disabled, nested],
  )
  const updateAssignees = useCallback(
    (selectedAssignees: AssigneePickerAssignee[]) => {
      const commonIds = new Set(commonAssignees.map(assignee => assignee.id))
      const selectedIds = new Set(selectedAssignees.map(assignee => assignee.id))
      const applyAssigneeIds = [...selectedIds].filter(id => !commonIds.has(id))
      const removeAssigneeIds = [...commonIds].filter(id => !selectedIds.has(id))
      if (applyAssigneeIds.length > 0 || removeAssigneeIds.length > 0) {
        onUpdate({applyAssigneeIds, removeAssigneeIds})
      }
    },
    [commonAssignees, onUpdate],
  )
  const handleSelectionChange = useCallback(
    (selectedAssignees: AssigneePickerAssignee[]) => {
      const copilotAssignee = selectedAssignees.find(assignee => assignee.isCopilot)
      if (copilotAssignee && canUseCopilotAssignment) {
        setCopilotAssigneeId(copilotAssignee.id)
        setCopilotModalOpen(true)
        setPickerInstanceKey(current => current + 1)
        updateAssignees(selectedAssignees.filter(assignee => !assignee.isCopilot))
      } else {
        updateAssignees(selectedAssignees)
      }
    },
    [canUseCopilotAssignment, updateAssignees],
  )

  return (
    <>
      <AssigneeRepositoryPicker
        key={`bulk-assignee-picker-${pickerInstanceKey}`}
        anchorElement={anchorElement}
        title="Apply assignees"
        repo={repo}
        owner={owner}
        readonly={disabled}
        onSelectionChange={handleSelectionChange}
        assignees={commonAssignees}
        assigneeTokens={[]}
        includeAssignableBots
        includeAuthorableBots={false}
        viewer={null}
        nested={nested}
        responsiveOnNarrow
      />
      {canUseCopilotAssignment && copilotModalOpen && issueNumbersWithoutCopilot.length > 0 && (
        <CopilotAssignmentModal
          isOpen={copilotModalOpen}
          onClose={() => setCopilotModalOpen(false)}
          repository={{name: repo, owner: {login: owner}}}
          issueNumbers={issueNumbersWithoutCopilot}
          onAssignmentComplete={() => setCopilotModalOpen(false)}
          returnFocusRef={assignButtonRef}
        />
      )}
    </>
  )
}
